import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { Controller, Control } from 'react-hook-form';
import { useProductMutations } from 'src/services/productsNew';
import { getProductImageUrl } from 'src/services/products';
import Icon from 'src/components/ui/Icon/Icon';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { useTranslation } from 'react-i18next';
import Image, { clearCachedImage } from 'src/components/ui/Image/Image';
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES,
  IMAGE_UPLOAD_MAX_FILE_SIZE_MB,
  IMAGE_UPLOAD_TYPES,
  isAllowedImageType,
  isWithinImageSizeLimit,
} from 'src/utils/imageUpload';
import styles from './ProductImage.module.scss';

export const PRODUCT_IMAGE_MAX_FILE_SIZE_BYTES = IMAGE_UPLOAD_MAX_FILE_SIZE_BYTES;
export const PRODUCT_IMAGE_TYPES = IMAGE_UPLOAD_TYPES;

interface ProductImageProps {
  product_id?: number;
  disabled?: boolean;
  control: Control<any>; // Todo
  name: string;
  productImage: string;
}

const ProductImage: React.FC<ProductImageProps> = ({ product_id, disabled, control, name, productImage }) => {
  const { t } = useTranslation();
  const numericId = Number(product_id);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(productImage);
  const [hasImage, setHasImage] = useState(false);
  const { updateProductImageAsync, isUploadingImage } = useProductMutations();

  useEffect(() => {
    setImagePreviewUrl(productImage);
  }, [productImage]);

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const blob = await updateProductImageAsync({
        params: {
          path: {
            product_id: numericId,
          },
        },
        body: formData as any,
        parseAs: 'blob',
      });
      clearCachedImage(getProductImageUrl(numericId));
      const imageUrl = URL.createObjectURL(blob as Blob);
      setImagePreviewUrl(imageUrl);

      toast.success(t('success.image_uploaded'));
    } catch (error) {
      toast.error(t('errors.image_upload_failed'));
    }
  };

  const handleFileSelect = (onChange: (file: File | null) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!isAllowedImageType(file)) {
      toast.error(t('errors.invalid_image_type'));
      return;
    }
    if (!isWithinImageSizeLimit(file)) {
      toast.error(t('errors.image_too_large', { size: IMAGE_UPLOAD_MAX_FILE_SIZE_MB }));
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    setImagePreviewUrl(fileUrl);
    onChange(file);
  };

  const handleImageRemove = (onChange: (file: File | null) => void) => {
    setImagePreviewUrl(null);
    setHasImage(false);
    onChange(null);
  };

  const renderImagePlaceholder = () => (
    <div className={styles.placeholderContainer}>
      <Icon name="imageUpload" width={40} height={40} />
      {!disabled && <span className="typo-text-14 text-faded text-center">{t('general.click_to_add')}</span>}
    </div>
  );

  const renderUploadButton = (value: File | null) => (
    <Button
      disabled={!value || isUploadingImage}
      className="mr-auto"
      title={t('general.upload_new_picture')}
      tone={ButtonToneProps.PRIMARY_BLUE}
      customIcon={
        isUploadingImage ? (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent" />
        ) : undefined
      }
      onClick={() => value && handleImageUpload(value)}
    />
  );

  const renderDeleteButton = (onChange: (file: File | null) => void, value: File | null) => (
    <Button
      className="mr-auto"
      disabled={!hasImage && !value}
      title={t('general.delete')}
      tone={ButtonToneProps.ERROR}
      onClick={() => handleImageRemove(onChange)}
    />
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <div className={classNames(styles.root)}>
          <div className={styles.imageContainer}>
            <Image
              imageUrl={imagePreviewUrl || ''}
              alt="product"
              fallbackComponent={renderImagePlaceholder()}
              onResolved={setHasImage}
            />
            <input disabled={disabled} type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={handleFileSelect(onChange)} />
          </div>
          {!disabled && (
            <div className="flex flex-col gap-24">
              {product_id && renderUploadButton(value)}
              {renderDeleteButton(onChange, value)}
            </div>
          )}
          {error && <p className="status-error-full mt-2">{error.message}</p>}
        </div>
      )}
    />
  );
};

export default ProductImage;
